import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../ampli';
import BLANK_TRACTOR from '../../../assets/images/black-tractor.jpg';
import { Button } from '../../../components/common/buttons/Button';
import { BottomFloatingButton } from '../../../components/common/buttons/FloatingButtons/BottomFloatingButton';
import { MainHead } from '../../../components/common/head/MainHead';
import { Icon } from '../../../components/common/icon/Icon';
import { Loading } from '../../../components/common/loading/Loading';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { MarketPriceFilterOptionValueSorterEnum } from '../../../constants/filter/marketPriceFilter.constant';
import {
  filterSliderConstant,
  priceSliderValueMapping,
  PriceSliderValueMappingType,
} from '../../../constants/filter/slider/filterSliderConstant';
import { MachineApprovalStatusType } from '../../../constants/machine/machine.constant';
import { marketPricePageInfo, pageInfo, sellTractorPageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { ProductStatusConstant } from '../../../constants/product/product.constant';
import { useGetMachinePriceStatistics } from '../../../hooks/api/marketPrice/queries/useGetMachinePriceStatistics.query';
import { useGetMachinePriceStatisticsById } from '../../../hooks/api/marketPrice/queries/useGetMachinePriceStatisticsById.query';
import { useInfiniteOpenMarketProducts } from '../../../hooks/api/product/queries/useInfiniteOpenMarketProducts';
import { useGetDownloadAppModalHeight } from '../../../hooks/modal/useGetDownloadAppModalHeight';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useAppDispatch } from '../../../hooks/store/useStore';
import { useCheckUserLoggedIn } from '../../../hooks/user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../../../hooks/user/useGetUserProfile';
import { useNotification } from '../../../provider/ToastNotificationProvider';
import { userSellTractorSurveySlice } from '../../../store/userSellTractorSurveySlice';
import { formatPrice } from '../../../utils/formatter/formatPrice.util';
import { getIQRBounds } from '../../../utils/getter/getIQRBounds';
import { getTractorEngineOutputInPsRange } from '../../../utils/getter/getTractorEngineOutputInPsRange.util';
import { postMessageToRN } from '../../../utils/RNCommunicator/postMessageToRN.util';
import { SectionTitle } from '../../../views/Product/detailView/ProductDetailView/components/SectionTitle';
import { OpenMarketImageOnlySwiper } from '../../../views/Product/listView/OpenMarketImageOnlySwiper';
import {
  IProductForOverviewCard,
  ProductOverviewCard,
} from '../../../views/Product/listView/ProductOverviewCard/ProductOverviewCard';
import { ProductOverviewCardSwiper } from '../../../views/Product/listView/ProductOverviewCard/ProductOverviewCardSwiper';
import { TractorValuePreviewCardSwiper } from '../../../views/Product/listView/TractorValuePreviewCard/TractorValuePreviewCardSwiper';

const FIXED_HEADER_HEIGHT = 48;

export const MarketPriceDetailPage = () => {
  const navigate = useNavigate();
  const [showGoToSellTractorModal, setShowGoToSellTractorModal] = useState<boolean>(false);

  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const { userProfile } = useGetUserProfile();
  const { openNotification } = useNotification();
  const downloadAppModalHeight = useGetDownloadAppModalHeight();

  const dispatch = useAppDispatch();
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;

  const { machineId } = useParams();
  const {
    data: machinePriceStatistics,
    isLoading,
    error: machinePriceStatisticsQueryError,
  } = useGetMachinePriceStatisticsById({
    machineId: machineId as string,
  });

  const brandId = machinePriceStatistics?.brand.id;
  const currentMachineEngineOutputInPs = machinePriceStatistics?.machine.engineOutputInPs;

  const { rangeStart, rangeEnd } = getTractorEngineOutputInPsRange(currentMachineEngineOutputInPs ?? 0);

  const { data: sameEngineOutputInPsRangeMachinePriceList } = useGetMachinePriceStatistics({
    searchParam: {
      minEngineOutputPs: rangeStart,
      maxEngineOutputPs: rangeEnd,
      minMedianPrice: filterSliderConstant.medianPrice.min,
      maxMedianPrice: priceSliderValueMapping[filterSliderConstant.medianPrice.max as PriceSliderValueMappingType],
      sort: MarketPriceFilterOptionValueSorterEnum.default_desc,
      page: 1,
      count: 100,
    },
    options: {
      enabled: !!brandId && !!currentMachineEngineOutputInPs,
      select: (data) => {
        if (!currentMachineEngineOutputInPs) return data;

        return {
          ...data,
          pages: data.pages.map((page) =>
            page
              .filter((item) => item.machine.id !== machineId)
              .sort((a, b) => {
                // 엔진 출력 차이 계산
                const aDiff = Math.abs((a.machine.engineOutputInPs ?? 0) - currentMachineEngineOutputInPs);
                const bDiff = Math.abs((b.machine.engineOutputInPs ?? 0) - currentMachineEngineOutputInPs);

                // 차이가 다르면 차이가 적은 순으로 정렬
                if (aDiff !== bDiff) {
                  return aDiff - bDiff;
                }

                // 차이가 같으면 priceCount가 높은 순으로 정렬
                return (b.priceStatistics?.priceCount ?? 0) - (a.priceStatistics?.priceCount ?? 0);
              }),
          ),
        };
      },
    },
  });

  const tractorInfo = machinePriceStatistics
    ? `${machinePriceStatistics.brand.name} ${machinePriceStatistics.machine.model} (${currentMachineEngineOutputInPs}마력)`
    : '';

  const { data: openMarketProducts } = useInfiniteOpenMarketProducts({
    searchParam: {
      status: `${ProductStatusConstant.ONSALE},${ProductStatusConstant.SOLDOUT}`,
      count: 100,
      machineType: 'tractor',
      machineId: machineId as string,
    },
  });

  const openMarketProductsForThisMachine = openMarketProducts?.pages[0].sort(
    (a, b) => new Date(b.approvalDate).getTime() - new Date(a.approvalDate).getTime(),
  );

  const handleSellTractorButtonClick = () => {
    ampli.pvmClickSellSameModelOnMarketPrice({
      brand: machinePriceStatistics?.brand.name ?? 'no_brand',
      model: machinePriceStatistics?.machine.model ?? 'no_model',
      highest_price: machinePriceStatistics?.maxPrice ?? 0,
      hp: machinePriceStatistics?.maxPrice ?? 0,
      lowest_price: machinePriceStatistics?.minPrice ?? 0,
    });

    setShowGoToSellTractorModal(true);
  };

  const goToSellTractorOverviewPage = () => {
    dispatch(
      userSellTractorSurveyActions.changeSellTractorRequiredInputs({
        brand: machinePriceStatistics?.brand.name,
        brandId: machinePriceStatistics?.brand.id,
        model: machinePriceStatistics?.machine.model,
        engineOutputInPs: machinePriceStatistics?.machine.engineOutputInPs,
        sellTractorMachineInfo: {
          id: machinePriceStatistics?.machine.id as string,
          approvalStatus: machinePriceStatistics?.machine.approvalStatus as MachineApprovalStatusType,
          engineOutputInPs: machinePriceStatistics?.machine.engineOutputInPs as number,
        },
      }),
    );
    navigate(`${sellTractorPageInfo.overviewPage.path}?type=tractor`);
  };

  useLayout({
    fixedHeader: (
      <MainHead
        backButton={{ isShow: true, directlyPassedURL: marketPricePageInfo.listPage.path }}
        headerTitle={tractorInfo}
        headerHeight={FIXED_HEADER_HEIGHT}
      />
    ),
    fixedFooter: (
      <BottomFloatingButton
        handleButtonClick={handleSellTractorButtonClick}
        icon={<Icon name={'Plus2'} width={20} height={20} />}
        title={'이 트랙터 판매하기'}
      />
    ),
    fixedHeaderHeight: FIXED_HEADER_HEIGHT,
    fixedFooterHeight: 0,
  });

  const handleShareButtonClick = () => {
    ampli.pvmClickMarketPriceDetailShareButton({
      model: machinePriceStatistics?.machine.model ?? 'no_model',
    });

    const pageUrlWithUtm = `${window.location.href}?utm_source=pvm_contents&utm_medium=information&utm_campaign=price_share&utm_term=250519`;

    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'share', messageParam: pageUrlWithUtm });
    } else {
      window.navigator.clipboard.writeText(pageUrlWithUtm);
      openNotification({ message: '현재 게시물의 주소가 복사되었습니다.' });
    }
  };

  const goToLoginPage = ({ buttonType }: { buttonType: 'highestLowestPrice' | 'supplyPrice' | 'newTractorPrice' }) => {
    ampli.pvmClickMarketPriceDetailLoginButton({
      buttonType,
      model: machinePriceStatistics?.machine.model ?? 'no_model',
    });
    navigate(pageInfo.loginPage.path);
  };

  const fommatedSellPrice = machinePriceStatistics?.sellPrices?.map((data) => {
    return { targetNumber: data.price };
  });

  const fommatedSuggestedPrice = machinePriceStatistics?.suggestedPrices?.map((data) => {
    return { targetNumber: data.price };
  });

  const fommatedNewTractorPrice = machinePriceStatistics?.newTractorPrices?.map((data) => {
    return { targetNumber: data.price };
  });

  const formmatedPrices = [
    ...(fommatedSellPrice ?? []),
    ...(fommatedSuggestedPrice ?? []),
    ...(fommatedNewTractorPrice ?? []),
  ];

  const { upperBound: firstUpperBound, lowerBound: firstLowerBound } = getIQRBounds(formmatedPrices);

  if (isLoading) return <Loading loading={isLoading} />;

  if (machinePriceStatisticsQueryError || !machinePriceStatistics) {
    return <Navigate to={marketPricePageInfo.listPage.path} replace />;
  }

  return (
    <>
      <MainImage
        src={machinePriceStatistics?.machine.imageMain || machinePriceStatistics?.brand.image || BLANK_TRACTOR}
        alt={'tractor-main-image'}
      />

      <TractorInfoContainer>
        <Title>{tractorInfo}</Title>

        {/* [PDS] notification 공통 컴포넌트 구현 시 교체 (https://gint-team.atlassian.net/browse/PVM-3167) */}
        <PriceInfoCaution>
          <IconWrapper>
            <Icon name={'CircleExclamation'} size={'Medium'} fill={'#FF3049'} />
          </IconWrapper>
          <PriceInfoCautionText>
            {
              '제공된 시세와 신차가격은 플루바마켓 데이터 및 시장 정보를 수집하여 재가공 한 내용입니다. 실제 가격은 판매자/대리점에 따라 변동될 수 있음을 유의해 주세요.'
            }
          </PriceInfoCautionText>
        </PriceInfoCaution>

        <MachinePriceContainer isBlurred={!isUserLoggedIn}>
          {!isUserLoggedIn && (
            <BlurContainer onClick={() => goToLoginPage({ buttonType: 'highestLowestPrice' })}>
              <LoginToCheckPrice>{'지금 로그인하고 시세부터\n신차 가격 까지 확인하기'}</LoginToCheckPrice>
              <KakaoLogin />
            </BlurContainer>
          )}
          <MachineBackground>
            <MachinePriceTextContainer>
              <Icon name={'AngleFillDown'} fill={'#445EFF'} size={'Large'} />
              <MachinePriceText>{'최저가'}</MachinePriceText>
            </MachinePriceTextContainer>
            <MachinePriceAmount>{`${formatPrice(machinePriceStatistics?.minPrice ?? 0)}원`}</MachinePriceAmount>
          </MachineBackground>
          <MachineBackground>
            <MachinePriceTextContainer>
              <Icon name={'AngleFillUp'} fill={'#FF3049'} size={'Large'} />
              <MachinePriceText>{'최고가'}</MachinePriceText>
            </MachinePriceTextContainer>
            <MachinePriceAmount>{`${formatPrice(machinePriceStatistics?.maxPrice ?? 0)}원`}</MachinePriceAmount>
          </MachineBackground>
        </MachinePriceContainer>

        <Button
          borderType={'square'}
          color={'outlinedGreyLight'}
          size={'lg'}
          icon={{ fileName: 'Link', size: 'Large', fill: '#C6C6D5', position: 'left' }}
          text={{ size: 'lg', content: '시세 공유하기' }}
          onClick={() => handleShareButtonClick()}
        />
      </TractorInfoContainer>

      {machinePriceStatistics.relatedProducts && machinePriceStatistics.relatedProducts.length > 0 && (
        <PluvaProductContainer>
          <SectionTitle>{`할부 구매 가능한 ${machinePriceStatistics.brand.name} 프리미엄 매물`}</SectionTitle>

          {machinePriceStatistics.relatedProducts.length === 1 ? (
            <ProductOverviewCard
              pageType={'mpdp'}
              product={machinePriceStatistics.relatedProducts[0] as IProductForOverviewCard}
            />
          ) : (
            <ProductOverviewCardSwiper
              category={'pluva'}
              pageType={'mpdp'}
              products={machinePriceStatistics.relatedProducts}
            />
          )}
        </PluvaProductContainer>
      )}

      {userProfile?.isAdmin && (
        <ForAdmin>
          <ForAdminTextBold>{'*어드민에게만 표시됩니다.'}</ForAdminTextBold>
          <ForAdminTextBold>{'아래 값이 구매 희망가에 적용됩니다.'}</ForAdminTextBold>
          <ForAdminText>{`이 모델의 상한가 : ${firstUpperBound.toLocaleString()}원`}</ForAdminText>
          <ForAdminText>{`이 모델의 하한가 : ${firstLowerBound.toLocaleString()}원`}</ForAdminText>
        </ForAdmin>
      )}

      <PriceSectionContainer>
        <PriceSectionHeader>
          <Row>
            <Title isFlexed>{'이 모델의 판매 희망가'}</Title>
            <Title>{`총 ${machinePriceStatistics?.sellPrices?.length || 0}건`}</Title>
          </Row>
          <Row gap={2}>
            <Icon name={'CircleExclamation'} size={'Medium'} fill={'#C6C6D5'} />
            <PriceDescription>{'실제 보유자들이 판매하고자 하는 가격이예요.'}</PriceDescription>
          </Row>
        </PriceSectionHeader>

        <PriceSectionTableContainer isBlurred={!isUserLoggedIn}>
          {!isUserLoggedIn && (
            <BlurContainer onClick={() => goToLoginPage({ buttonType: 'supplyPrice' })}>
              <LoginToCheckPrice color={'#FF3049'}>{'로그인하고 시세를 확인하세요'}</LoginToCheckPrice>
              <KakaoLogin />
            </BlurContainer>
          )}

          <PriceTableHeader
            isSticky={isUserLoggedIn}
            fixedHeaderHeight={FIXED_HEADER_HEIGHT}
            downloadAppModalHeight={downloadAppModalHeight}
          >
            <PriceTableHeaderTextContainer>
              <PriceTableHeaderText>{'연식 ·'}</PriceTableHeaderText>
              <PriceTableHeaderText>{'사용시간'}</PriceTableHeaderText>
            </PriceTableHeaderTextContainer>
            <PriceTableHeaderText textAlign={'right'}>{'등록연도/월'}</PriceTableHeaderText>
            <PriceTableHeaderText minWidth={110} textAlign={'right'}>
              {'판매 희망가'}
            </PriceTableHeaderText>
          </PriceTableHeader>

          <PriceTableBody>
            {machinePriceStatistics?.sellPrices
              .sort((a, b) => b.price - a.price)
              .map((sellPrice) => {
                return (
                  <PriceTableBodyRow key={sellPrice.createdAt + sellPrice.manufacturedYear}>
                    <PriceTableBodyRowTextContainer>
                      <PriceTableBodyRowText>{`${sellPrice.manufacturedYear
                        .toString()
                        .slice(-2)}년식 ·`}</PriceTableBodyRowText>
                      <PriceTableBodyRowText>{`${sellPrice.operationTime.toLocaleString()}시간`}</PriceTableBodyRowText>
                    </PriceTableBodyRowTextContainer>
                    <PriceTableBodyRowText minWidth={60} textAlign={'right'}>
                      {`${sellPrice.createdAt.split('-')[0].slice(-2)}/${sellPrice.createdAt.split('-')[1]}`}
                    </PriceTableBodyRowText>
                    <PriceTableBodyRowText minWidth={110} textAlign={'right'} isBold>
                      {`${formatPrice(sellPrice.price)}원`}
                    </PriceTableBodyRowText>
                  </PriceTableBodyRow>
                );
              })}
          </PriceTableBody>
        </PriceSectionTableContainer>
      </PriceSectionContainer>

      {machinePriceStatistics?.suggestedPrices && machinePriceStatistics?.suggestedPrices.length > 0 && (
        <PriceSectionContainer>
          <PriceSectionHeader>
            <Row>
              <Title isFlexed>{'이 모델의 구매 희망가'}</Title>
              <Title>{`총 ${
                machinePriceStatistics?.suggestedPrices?.filter(
                  (price) => price.price <= firstUpperBound && price.price >= firstLowerBound,
                ).length || 0
              }건`}</Title>
            </Row>
            <Row gap={2}>
              <Icon name={'CircleExclamation'} size={'Medium'} fill={'#C6C6D5'} />
              <PriceDescription>{'사람들이 구매하고자 제안한 가격이예요.'}</PriceDescription>
            </Row>
          </PriceSectionHeader>

          <PriceSectionTableContainer isBlurred={!isUserLoggedIn}>
            {!isUserLoggedIn && (
              <BlurContainer onClick={() => goToLoginPage({ buttonType: 'supplyPrice' })}>
                <LoginToCheckPrice color={'#FF3049'}>{'로그인하고 시세를 확인하세요'}</LoginToCheckPrice>
                <KakaoLogin />
              </BlurContainer>
            )}

            <PriceTableHeader
              isSticky={isUserLoggedIn}
              fixedHeaderHeight={FIXED_HEADER_HEIGHT}
              downloadAppModalHeight={downloadAppModalHeight}
            >
              <PriceTableHeaderTextContainer>
                <PriceTableHeaderText>{'연식 ·'}</PriceTableHeaderText>
                <PriceTableHeaderText>{'사용시간'}</PriceTableHeaderText>
              </PriceTableHeaderTextContainer>
              <PriceTableHeaderText textAlign={'right'}>{'등록연도/월'}</PriceTableHeaderText>
              <PriceTableHeaderText minWidth={110} textAlign={'right'}>
                {'구매 희망가'}
              </PriceTableHeaderText>
            </PriceTableHeader>

            <PriceTableBody>
              {machinePriceStatistics?.suggestedPrices
                .filter((price) => price.price < firstUpperBound && price.price > firstLowerBound)
                .sort((a, b) => b.price - a.price)
                .map((suggestedPrice) => {
                  return (
                    <PriceTableBodyRow key={suggestedPrice.createdAt + suggestedPrice.manufacturedYear}>
                      <PriceTableBodyRowTextContainer>
                        <PriceTableBodyRowText>{`${suggestedPrice.manufacturedYear
                          .toString()
                          .slice(-2)}년식 ·`}</PriceTableBodyRowText>
                        <PriceTableBodyRowText>{`${suggestedPrice.operationTime.toLocaleString()}시간`}</PriceTableBodyRowText>
                      </PriceTableBodyRowTextContainer>
                      <PriceTableBodyRowText minWidth={60} textAlign={'right'}>
                        {`${suggestedPrice.createdAt.split('-')[0].slice(-2)}/${
                          suggestedPrice.createdAt.split('-')[1]
                        }`}
                      </PriceTableBodyRowText>
                      <PriceTableBodyRowText minWidth={110} textAlign={'right'} isBold>
                        {`${formatPrice(suggestedPrice.price)}원`}
                      </PriceTableBodyRowText>
                    </PriceTableBodyRow>
                  );
                })}
            </PriceTableBody>
          </PriceSectionTableContainer>
        </PriceSectionContainer>
      )}

      <PriceSectionContainer>
        <PriceSectionHeader>
          <Title isFlexed>{'이 모델의 신차 참고가'}</Title>
        </PriceSectionHeader>

        <PriceSectionTableContainer isBlurred={!isUserLoggedIn}>
          {!isUserLoggedIn && (
            <BlurContainer onClick={() => goToLoginPage({ buttonType: 'newTractorPrice' })}>
              <LoginToCheckPrice color={'#FF3049'}>{'로그인하고 시세를 확인하세요'}</LoginToCheckPrice>
              <KakaoLogin />
            </BlurContainer>
          )}
          {machinePriceStatistics?.newTractorPrices && machinePriceStatistics?.newTractorPrices.length > 0 && (
            <>
              <PriceTableHeader>
                <PriceTableHeaderTextContainer>
                  <PriceTableHeaderText>{'연식'}</PriceTableHeaderText>
                </PriceTableHeaderTextContainer>
                <PriceTableHeaderText textAlign={'right'}>{'신품가'}</PriceTableHeaderText>
              </PriceTableHeader>

              <PriceTableBody>
                {machinePriceStatistics?.newTractorPrices.map((newTractorPrice) => {
                  return (
                    <PriceTableBodyRow key={newTractorPrice.year}>
                      <PriceTableBodyRowTextContainer>
                        <PriceTableBodyRowText>{`${newTractorPrice.year
                          .toString()
                          .slice(-2)}년식`}</PriceTableBodyRowText>
                      </PriceTableBodyRowTextContainer>
                      <PriceTableBodyRowText minWidth={110} textAlign={'right'} isBold>
                        {`${formatPrice(newTractorPrice.price)}원`}
                      </PriceTableBodyRowText>
                    </PriceTableBodyRow>
                  );
                })}
              </PriceTableBody>
            </>
          )}
          {machinePriceStatistics?.newTractorPrices && machinePriceStatistics?.newTractorPrices.length === 0 && (
            <NoTableData text={'아직 신차 가격정보가 없어요'} />
          )}
        </PriceSectionTableContainer>
      </PriceSectionContainer>

      {openMarketProductsForThisMachine && openMarketProductsForThisMachine.length > 0 && (
        <PriceSectionContainer>
          <PriceSectionHeader>
            <Row>
              <Title isFlexed>{'실제 매물 사진 구경하기'}</Title>
            </Row>
            <Row gap={2}>
              <Icon name={'CircleExclamation'} size={'Medium'} fill={'#C6C6D5'} />
              <span>
                <PriceDescription>{'플루바 마켓'}</PriceDescription>
                <PriceDescription fontWeight={700}>{' 직거래에 올라온 '}</PriceDescription>
                <PriceDescription>{'매물들이에요.'}</PriceDescription>
              </span>
            </Row>
          </PriceSectionHeader>

          <SellTractorContainer>
            {!isUserLoggedIn && (
              <BlurContainer onClick={() => goToLoginPage({ buttonType: 'supplyPrice' })} style={{ zIndex: 50 }}>
                <LoginToCheckPrice color={'#FF3049'}>{'로그인하고 사진을 확인하세요'}</LoginToCheckPrice>
                <KakaoLogin />
              </BlurContainer>
            )}

            <OpenMarketImageOnlySwiper openMarketProducts={openMarketProductsForThisMachine} pageType={'odp'} />
          </SellTractorContainer>
        </PriceSectionContainer>
      )}

      {machinePriceStatistics.relatedTractorMarketPrice &&
        machinePriceStatistics.relatedTractorMarketPrice.length > 0 && (
          <SameBrandTractorContainer>
            <PriceSectionHeader>
              <Title isFlexed>{'같은 브랜드 시세 살펴보기'}</Title>
            </PriceSectionHeader>
            <TractorValuePreviewCardSwiper
              products={machinePriceStatistics.relatedTractorMarketPrice}
              type={'same-brand'}
            />
          </SameBrandTractorContainer>
        )}

      {sameEngineOutputInPsRangeMachinePriceList && sameEngineOutputInPsRangeMachinePriceList.pages[0].length > 0 && (
        <SameBrandTractorContainer>
          <PriceSectionHeader>
            <Title isFlexed>{'비슷한 마력대 시세 살펴보기'}</Title>
          </PriceSectionHeader>
          <TractorValuePreviewCardSwiper
            products={sameEngineOutputInPsRangeMachinePriceList.pages[0]}
            type={'similar-engine-output'}
          />
        </SameBrandTractorContainer>
      )}

      <PopupConfirmModal
        isShow={showGoToSellTractorModal}
        hasCloseButton={false}
        closeModal={() => setShowGoToSellTractorModal(false)}
        title={'지금 보고있는 트랙터로\n판매 등록을 시작할게요'}
        element={
          <TractorTitleBox>
            <TractorTitle>{`${machinePriceStatistics.brand.name} ${machinePriceStatistics.machine.model}`}</TractorTitle>
            <TractorTitle>{`(${machinePriceStatistics.machine.engineOutputInPs}마력)`}</TractorTitle>
          </TractorTitleBox>
        }
        buttonArray={[
          {
            text: '판매 등록 하기',
            onClick: goToSellTractorOverviewPage,
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: () => {
              setShowGoToSellTractorModal(false);
            },
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const KakaoLogin = () => {
  return (
    <KakaoLoginButton>
      <KakaoIconWrapper>
        <Icon name={'KakaoTalk'} width={24} height={24} />
      </KakaoIconWrapper>
      <KakakoLoginText>{'카카오로 3초만에 로그인'}</KakakoLoginText>
    </KakaoLoginButton>
  );
};

const NoTableData = ({ text }: { text: string }) => {
  return (
    <NoTableDataContainer>
      <Icon name={'BookBox'} width={60} height={61} />
      <NoTableDataText>{text}</NoTableDataText>
    </NoTableDataContainer>
  );
};

const NoTableDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  padding: 40px 0px 24px 0px;
`;

const NoTableDataText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);
  text-align: center;

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
`;

const TractorInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  padding: 32px 16px;
`;

const MachinePriceContainer = styled.div<{ isBlurred: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;

  ${({ isBlurred }) =>
    isBlurred &&
    `
    position: relative;
    padding: 32px 0px;
    `}
`;

const MachineBackground = styled.div`
  display: flex;
  padding: 16px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const MachinePriceTextContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
`;

const MachinePriceText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;

const MachinePriceAmount = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const Title = styled.span<{ isFlexed?: boolean }>`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */

  ${({ isFlexed }) =>
    isFlexed &&
    `
    flex: 1 0 0;
  `}
`;

const PriceInfoCaution = styled.div`
  display: flex;
  padding: 8px;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-secondary-red, #ffdbe0);
`;

const PriceInfoCautionText = styled.span`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);

  /* Body/Small/s-regular */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 18px */
`;

const IconWrapper = styled.div`
  display: flex;
  width: 18px;
  height: 18px;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const PriceSectionTableContainer = styled.div<{ isBlurred?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  ${({ isBlurred }) =>
    isBlurred &&
    `
    position: relative;
    `}
`;

const PriceSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  padding: 0px 16px 32px 16px;
`;

const PriceSectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const Row = styled.div<{ gap?: number }>`
  display: flex;
  align-items: center;
  align-self: stretch;

  gap: ${(props) => props.gap ?? 0}px;
`;

const PriceDescription = styled.span<{ fontWeight?: number }>`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Display/Small/s-regular */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: ${(props) => props.fontWeight ?? 400};
  line-height: 130%; /* 15.6px */
`;

const PriceTableHeader = styled.div<{
  isSticky?: boolean;
  fixedHeaderHeight?: number;
  downloadAppModalHeight?: number;
}>`
  display: flex;
  align-items: center;
  align-self: stretch;

  ${({ isSticky }) =>
    isSticky &&
    `
    position: sticky;
    z-index: 10;
  `}

  ${({ fixedHeaderHeight, downloadAppModalHeight }) =>
    `
    top: ${(fixedHeaderHeight ?? 0) + (downloadAppModalHeight ?? 0)}px;
  `}

  padding: 8px 16px;
  padding: 8px 16px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const PriceTableHeaderTextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1 0 0;

  @media screen and (max-width: 340px) {
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }
`;

const PriceTableHeaderText = styled.span<{ minWidth?: number; textAlign?: 'left' | 'right' }>`
  min-width: ${(props) => props.minWidth ?? 0}px;
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */

  text-align: ${(props) => props.textAlign ?? 'left'};
`;

const PriceTableBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  padding: 8px 16px 0px 16px;
`;

const PriceTableBodyRow = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;

  padding: 8px 0px;
`;

const PriceTableBodyRowText = styled.span<{ minWidth?: number; textAlign?: 'left' | 'right'; isBold?: boolean }>`
  min-width: ${(props) => props.minWidth ?? 0}px;

  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: ${(props) => (props.isBold ? 700 : 400)};
  line-height: 130%; /* 18.2px */

  text-align: ${(props) => props.textAlign ?? 'left'};
`;

const PriceTableBodyRowTextContainer = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 2px;
  flex: 1 0 0;
  flex-wrap: wrap;

  @media screen and (max-width: 340px) {
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    flex-wrap: nowrap;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
`;

const BlurContainer = styled.div`
  width: calc(100% + 8px);
  height: calc(100% + 8px);

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;

  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(4px);

  position: absolute;
  left: -4px;
  top: -4px;

  cursor: pointer;
`;
const KakaoLoginButton = styled.button`
  width: 250px;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px 8px;

  gap: 10px;

  position: relative;

  border-radius: var(--radius-medium, 8px);
  background: #ffd337;
`;

const KakaoIconWrapper = styled.div`
  position: absolute;
  left: 8px;
  top: 8px;
`;

const KakakoLoginText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */
`;

const LoginToCheckPrice = styled.pre<{ color?: string }>`
  color: ${(props) => props.color ?? 'var(--Light-Scheme-Monochrome-black, #232429)'};
  text-align: center;

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 26px */
`;

const PluvaProductContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  gap: 16px;
  padding: 0 16px 32px 16px;
`;

const SameBrandTractorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  padding: 0 16px 32px 16px;
`;

const TractorTitleBox = styled.div`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  flex-wrap: wrap;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);

  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;
`;

const TractorTitle = styled.span`
  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  white-space: nowrap;
`;

const SellTractorContainer = styled.div`
  width: 100%;

  position: relative;
`;

const ForAdmin = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  padding: 8px;
`;

const ForAdminText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Small/s-regular */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 15.6px */
`;

const ForAdminTextBold = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Small/s-regular */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 800;
  line-height: 130%; /* 15.6px */
`;
